import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from "react-leaflet";
import { Download, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { SecureGate, SecureLockButton } from "@/components/SecureGate";

const SOURCE = { job_start: "Avvio intervento", job_close: "Chiusura intervento", auto: "Rilevamento", entrata: "Entrata", uscita: "Uscita" };
const pad = (n) => String(n).padStart(2, "0");
const local = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const startOfDay = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" });
const hm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });

export function StopsList({ stops }) {
  if (!stops?.length) return null;
  return (
    <div className="rounded-2xl border bg-white" data-testid="route-stops">
      <p className="border-b px-4 py-3 text-sm font-semibold">Soste (almeno 10 minuti)</p>
      <div className="divide-y text-sm">
        {stops.map((s, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2" data-testid={`route-stop-${i}`}>
            <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border-2 border-[#0f1710] bg-amber-400" />{hm(s.from)}–{hm(s.to)}</span>
            <span className="font-mono">{s.minutes} min</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Fit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length) map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [30, 30], maxZoom: 16 });
  }, [points, map]);
  return null;
}

export function RouteMap({ data, height = "h-[460px]" }) {
  const byDay = data.days.map((d) => ({ ...d, pts: data.points.filter((p) => p.day === d.date) }));
  return (
    <div className={`${height} overflow-hidden rounded-2xl border`} data-testid="route-map">
      <MapContainer center={[41.0, 14.08]} zoom={11} className="h-full w-full">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {byDay.map((d) => d.pts.length > 1 && <Polyline key={d.date} positions={d.pts.map((p) => [p.lat, p.lng])} pathOptions={{ color: d.color, weight: 4, opacity: 0.85 }} />)}
        {data.points.map((p, i) => (
          <CircleMarker key={i} center={[p.lat, p.lng]} radius={p.source === "auto" ? 4 : 7} pathOptions={{ color: "#fff", weight: 2, fillColor: p.color, fillOpacity: 1 }}>
            <Popup><span className="text-xs">{SOURCE[p.source] || p.source} · {fmt(p.at)}</span></Popup>
          </CircleMarker>
        ))}
        {(data.stops || []).map((s, i) => (
          <CircleMarker key={`s${i}`} center={[s.lat, s.lng]} radius={11} pathOptions={{ color: "#0f1710", weight: 2, fillColor: "#fbbf24", fillOpacity: 0.85 }}>
            <Popup><span className="text-xs">Sosta {s.minutes} min · {hm(s.from)}–{hm(s.to)}</span></Popup>
          </CircleMarker>
        ))}
        <Fit points={data.points} />
      </MapContainer>
    </div>
  );
}

export function DaysTable({ data }) {
  return (
    <div className="rounded-2xl border bg-white" data-testid="route-days">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <p className="text-sm font-semibold">Riepilogo per giorno</p>
        <p className="font-mono text-sm" data-testid="route-total-km">Totale: <b>{data.total_km} km</b> · {data.points.length} rilevamenti</p>
      </div>
      <div className="divide-y text-sm">
        {data.days.map((d, i) => (
          <div key={d.date} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-2.5" data-testid={`route-day-${i}`}>
            <span className="h-3 w-3 rounded-full" style={{ background: d.color }} />
            <span><span className="capitalize">{dayLabel(d.date)}</span> <span className="text-xs text-slate-500">· {hm(d.first)}–{hm(d.last)} · {d.points} punti</span></span>
            <span className="font-mono" data-testid={`route-day-km-${i}`}>{d.km} km</span>
          </div>
        ))}
        {!data.days.length && <p className="px-4 py-8 text-center text-slate-500" data-testid="route-empty">Nessun rilevamento nel periodo selezionato.</p>}
      </div>
      <p className="border-t px-4 py-2 text-[11px] text-slate-500">Km stimati in linea d'aria tra rilevamenti consecutivi.</p>
    </div>
  );
}

function RouteHistory() {
  const [emps, setEmps] = useState([]);
  const [q, setQ] = useState({ technician_id: "", start: local(startOfDay()), end: local(new Date()) });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/admin/tracking/employees").then((r) => setEmps(r.data)); }, []);
  const params = () => ({ technician_id: q.technician_id, start: new Date(q.start).toISOString(), end: new Date(q.end).toISOString() });
  const run = async (kind) => {
    if (!q.technician_id) return toast.error("Scegli un dipendente");
    setBusy(kind);
    try {
      if (kind === "view") setData((await api.get("/admin/tracking/range", { params: params() })).data);
      else {
        const { data: blob } = await api.get("/admin/tracking/range/pdf", { params: params(), responseType: "blob" });
        const url = URL.createObjectURL(blob);
        Object.assign(document.createElement("a"), { href: url, download: `percorso-${q.start.slice(0, 10)}.pdf` }).click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      toast.error(e.response?.data instanceof Blob ? "Impossibile generare il PDF" : errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const upd = (k) => (e) => setQ((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="space-y-5">
      <div className="grid gap-3 rounded-2xl border bg-white p-4 md:grid-cols-[1.3fr_1fr_1fr_auto] md:items-end">
        <div className="space-y-1"><Label>Dipendente</Label>
          <select value={q.technician_id} onChange={upd("technician_id")} className="w-full rounded-lg border bg-white px-3 py-2 text-sm" data-testid="route-employee-select">
            <option value="">Scegli un dipendente</option>
            {emps.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="space-y-1"><Label>Dal</Label><Input type="datetime-local" value={q.start} onChange={upd("start")} data-testid="route-start-input" /></div>
        <div className="space-y-1"><Label>Al</Label><Input type="datetime-local" value={q.end} onChange={upd("end")} data-testid="route-end-input" /></div>
        <div className="flex gap-2">
          <Button className="rounded-full" onClick={() => run("view")} disabled={!!busy} data-testid="route-show-button">{busy === "view" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Mostra</Button>
          <Button variant="outline" className="rounded-full" onClick={() => run("pdf")} disabled={!!busy} data-testid="route-pdf-button">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF</Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">Periodo massimo 30 giorni. Le posizioni sono registrate solo per i dipendenti, in orario di lavoro, e conservate 30 giorni.</p>
      {data && (
        <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
          <RouteMap data={data} />
          <div className="space-y-4"><DaysTable data={data} /><StopsList stops={data.stops} /></div>
        </div>
      )}
    </div>
  );
}

export default function AdminRoutes() {
  return (
    <div>
      <PageHeader eyebrow="Area protetta" title="Percorsi dipendenti"><SecureLockButton /></PageHeader>
      <SecureGate><RouteHistory /></SecureGate>
    </div>
  );
}
